import React, { useMemo } from 'react'
import { useMutation, useQuery } from '@apollo/client'
import { useParams } from 'react-router-dom'
import { PageLoader } from 'components/PageLoader'
import { convertToCents, convertToDollars } from 'utils/priceFormatter'
import { ImageField } from 'components/DropzoneField'
import { PRODUCTS_PATH_S3, PUBLIC_PREFIX } from 'constants/images'
import { useImageUpload } from 'utils/saveImageS3'
import { FIND_ADDITIONAL_TEST, UPDATE_ADDITIONAL_TEST } from 'queries/additionalTests'
import { GET_LIST_PRACTICE_TEST } from 'queries/practiceTest'
import { findOneAdditionalTest, findOneAdditionalTestVariables } from '__generatedTypes__/findOneAdditionalTest'
import { updateAdditionalTest, updateAdditionalTestVariables } from '__generatedTypes__/updateAdditionalTest'
import { findAllPracticeTest, findAllPracticeTestVariables } from '__generatedTypes__/findAllPracticeTest'
import { MembershipType, ProductStatus, UpdateAdditionalTestInput } from '__generatedTypes__/globalTypes'
import { cmsRoutes, useLink } from 'routes/cmsRoutes'
import { toast } from 'utils/toast'
import { useFiles } from '../../../SuccessStory/Edit/hooks'
import { Form, FormValues, STATUSES } from './Form'

export const Edit = () => {
  const { id } = useParams()
  const { navigate } = useLink()
  const uploadImage = useImageUpload()
  const {
    fetchFromLinks,
    avatarFile: imageLightFile,
    coverFile: imageDarkFile,
    isLoading: isLoadingImages,
  } = useFiles()

  const { data, loading } = useQuery<findOneAdditionalTest, findOneAdditionalTestVariables>(FIND_ADDITIONAL_TEST, {
    variables: {
      additionalTestId: String(id),
    },
    onCompleted: async (data) => {
      const additionalTest = data.findOneAdditionalTest

      await fetchFromLinks(
        {
          imageLink: additionalTest?.ligthModeImageUrl ?? '',
          initialLink: additionalTest?.ligthModeImageUrl ?? '',
        },
        {
          imageLink: additionalTest?.darkModeImageUrl ?? '',
          initialLink: additionalTest?.darkModeImageUrl ?? '',
        }
      )
    },
    onError: () => {
      navigate(cmsRoutes.PRODUCTS, undefined, {
        searchParams: new URLSearchParams({ tab: 'additional-tests' }),
      })
    },
    fetchPolicy: 'network-only',
    skip: !id,
  })
  const additionalTest = data?.findOneAdditionalTest

  const { data: practiceTestsData, loading: practiceTestsLoading } = useQuery<
    findAllPracticeTest,
    findAllPracticeTestVariables
  >(GET_LIST_PRACTICE_TEST, {
    variables: {
      params: {
        where: {
          subject: {
            id: String(additionalTest?.subject?.id),
          },
          isLive: true,
          membershipLevel: MembershipType.PremiumMax,
        },
      },
    },
    fetchPolicy: 'network-only',
    skip: !additionalTest?.subject?.id,
  })
  const premiumPTs = practiceTestsData?.findAllPracticeTest.items ?? []

  const [updatePauseTokenMutation] = useMutation<updateAdditionalTest, updateAdditionalTestVariables>(
    UPDATE_ADDITIONAL_TEST
  )

  const initialValues: FormValues | null = useMemo(() => {
    if (!additionalTest || isLoadingImages || practiceTestsLoading) {
      return null
    }

    return {
      type: additionalTest.type,
      name: additionalTest.name,
      description: additionalTest.description,
      subject: additionalTest.subject.tabName,
      practiceTestIds: additionalTest.practiceTests.map(({ id }) => id),
      price: additionalTest.price ? convertToDollars(additionalTest.price) : 0,
      status: STATUSES.find(({ id }) => id === additionalTest.status),
      isPopular: additionalTest.isMostPopular,
      imageLight: imageLightFile && [imageLightFile],
      imageDark: imageDarkFile && [imageDarkFile],
    }
  }, [loading, isLoadingImages, practiceTestsLoading])

  const getFullImagePath = (path?: string) => {
    if (!path) {
      return undefined
    }

    return `${process.env.REACT_APP_IMAGEKIT_URL}/tr:h-default,w-default/${PUBLIC_PREFIX}/${path}`
  }

  const uploadFiles = async (imageLight: ImageField | undefined, imageDark: ImageField | undefined) => {
    const imageLightFile = imageLight?.isUploaded ? null : imageLight?.file
    const imageDarkFile = imageDark?.isUploaded ? null : imageDark?.file

    const [imageLightPath, imageDarkPath] = await Promise.all([
      imageLightFile
        ? uploadImage(imageLightFile, PRODUCTS_PATH_S3).then(getFullImagePath)
        : Promise.resolve(imageLight?.initialValue),
      imageDarkFile
        ? uploadImage(imageDarkFile, PRODUCTS_PATH_S3).then(getFullImagePath)
        : Promise.resolve(imageDark?.initialValue),
    ])

    return [imageLightPath, imageDarkPath]
  }

  const handleSubmit = async (values: FormValues) => {
    try {
      const id = additionalTest?.id
      const subjectId = additionalTest?.subject.id

      if (!id || !subjectId) {
        return
      }

      const [imageLight, imageDark] = await uploadFiles(values.imageLight?.[0], values.imageDark?.[0])

      const changedValues: UpdateAdditionalTestInput = {
        name: values.name !== initialValues?.name ? values.name : undefined,
        description: values.description !== initialValues?.description ? values.description : undefined,
        practiceTests:
          values.practiceTestIds?.join(', ') !== initialValues?.practiceTestIds?.join(', ')
            ? values.practiceTestIds?.map((id) => ({ id })) ?? []
            : undefined,
        price: values.price !== initialValues?.price ? convertToCents(values.price ?? 0) : undefined,
        status:
          values.status?.id !== initialValues?.status?.id ? values.status?.id ?? ProductStatus.Inactive : undefined,
        isMostPopular: values.isPopular !== initialValues?.isPopular ? values.isPopular : undefined,
        ligthModeImageUrl: imageLight !== initialValues?.imageLight?.[0]?.file ? imageLight : undefined,
        darkModeImageUrl: imageDark !== initialValues?.imageDark?.[0]?.file ? imageDark : undefined,
      }
      const isChanged = Object.values(changedValues).filter((value) => value !== undefined).length

      if (isChanged) {
        await updatePauseTokenMutation({
          variables: {
            updateAdditionalTestInput: {
              id,
              subject: {
                id: subjectId,
              },
              ...changedValues,
            },
          },
        })

        toast.success('Additional test updated successfully')
      }

      navigate(cmsRoutes.PRODUCTS, undefined, {
        searchParams: new URLSearchParams({ tab: 'additional-tests' }),
      })
    } catch (error) {
      if (error instanceof Error && error.message.toLowerCase().includes('cannot deactivate')) {
        return { status: error.message }
      } else {
        toast.error('Failed to update additional test')
      }
    }
  }

  if (loading || isLoadingImages || practiceTestsLoading) {
    return <PageLoader />
  }

  if (!initialValues) {
    return null
  }

  return <Form initialValues={initialValues} onSubmit={handleSubmit} premiumPTs={premiumPTs} />
}
